import React from 'react';
import { IconButton, Menu } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { Styled } from '../gateway-card.styled';
import { activeMenus, pendingMenus, rejectedMenus } from '../constants';
import MenuItem from './menu-item';
import { useGatewayCardContext } from '../gateway-card.context';
import { useUserProfileQuery } from 'app/store';
import { usePermissionChecker } from 'utils/utils';
import useGatewayAccess from 'features/gateways/hooks/use-gateway-access';

const CardMenus: React.FC = () => {
    const {
        loading,
        gateway,
        menu: { open, close, isOpen, anchorEl },
        dialogs: { cancel },
    } = useGatewayCardContext();
    const { hasPermission } = usePermissionChecker();
    const { status, owner_id } = gateway;
    const { data: profile } = useUserProfileQuery();
    const {
        isOwner,
        canManageOverallSettings,
        canManageNotificationSettings,
    } = useGatewayAccess(gateway);

    const hasPersonalPaymentPermission = hasPermission(
        'gateway-paymentTools-show',
        gateway,
    );

    const renderMenus = () => {
        if (status === 'activated') {
            return activeMenus({
                overall: canManageOverallSettings,
                personal: isOwner && !!hasPersonalPaymentPermission,
                namad: isOwner,
                notifications: canManageNotificationSettings,
                admins: isOwner && profile?.id === owner_id,
                terminals: isOwner,
                contract: isOwner,
            }).map((menu) => (
                <MenuItem
                    key={menu.route + menu.label}
                    {...menu}
                    onClick={() => close(menu.route + '')}
                />
            ));
        }

        if (status === 'pending') {
            const menus: React.ReactElement[] = [];

            if (canManageOverallSettings) {
                menus.push(
                    <MenuItem
                        key="pending-overall"
                        {...pendingMenus[0]}
                        onClick={() => close('overall')}
                    />,
                );
            }

            if (isOwner) {
                menus.push(
                    <MenuItem
                        key="pending-delete"
                        {...pendingMenus[1]}
                        onClick={cancel.open}
                    />,
                );
            }

            return menus;
        }

        if (status === 'rejected' && isOwner) {
            return [
                <MenuItem
                    key="rejected-delete"
                    {...rejectedMenus[0]}
                    onClick={cancel.open}
                />,
            ];
        }

        return [];
    };

    const menus = renderMenus();

    if (menus.length === 0) {
        return null;
    }

    return (
        <Styled.CardButton>
            <IconButton
                onClick={(event) => {
                    event.stopPropagation();
                    open(event);
                }}
                size="small"
                disabled={loading}
                sx={(theme) => ({
                    zIndex: 10,
                    width: 40,
                    height: 40,
                    [theme.breakpoints.down('sm')]: {
                        width: 44,
                        height: 44,
                    },
                })}>
                <MoreVertIcon fontSize="small" />
            </IconButton>

            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={isOpen}
                onClose={() => close()}
                sx={(theme) => ({
                    '& .MuiBackdrop-root': {
                        backgroundColor:
                            theme.palette.mode === 'light'
                                ? 'rgba(15, 23, 42, 0.06)'
                                : 'rgba(0, 0, 0, 0.14)',
                        backdropFilter: 'blur(2px)',
                        WebkitBackdropFilter: 'blur(2px)',
                    },
                })}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 2,
                        },
                    },
                }}>
                {menus}
            </Menu>
        </Styled.CardButton>
    );
};

export default CardMenus;
